import { useCallback, useState } from "react";
import { ApiRequestError } from "../api/client.js";
import { useThrowSession } from "../api/useThrowSession.js";
import { useTransfers } from "../api/useTransfers.js";
import { TransferPanel } from "../components/TransferPanel.js";
import {
  Button,
  Card,
  Countdown,
  Eyebrow,
  Modal,
  PinDisplay,
  PinInput,
  Status,
  useToast,
} from "../design/components/index.js";

/**
 * Startseite des Throw-Bereichs.
 *
 * Drei Zustaende, die sich gegenseitig ausschliessen:
 * 1. Nicht verbunden -- eigene PIN wird angezeigt, fremde kann eingegeben werden
 * 2. Anfrage laeuft -- es wird auf die Entscheidung des Gegenuebers gewartet
 * 3. Verbunden -- die Kopplung besteht
 */
export function Home(): React.ReactElement {
  const { state, subscribe, actions } = useThrowSession();
  const transfers = useTransfers(subscribe, state.session?.pairing?.pairingId ?? null);
  const toast = useToast();

  const [pin, setPin] = useState("");
  const [pinError, setPinError] = useState<string | undefined>(undefined);
  const [busy, setBusy] = useState(false);

  const handleConnect = useCallback(
    async (value: string) => {
      if (busy) return;
      setBusy(true);
      try {
        const result = await actions.connect(value);
        if (result.kind === "vault") {
          setPin("");
          // Die Entscheidung faellt auf dem Server. Das Frontend folgt nur der
          // Adresse, die er liefert -- es bestimmt nie selbst, dass ein
          // Vault-Zugriff beginnt.
          if (result.available && result.redirectUrl) {
            toast.show("Weiterleitung zur Anmeldung…", "info");
            window.location.assign(result.redirectUrl);
          } else {
            toast.show("Der geschützte Bereich ist auf diesem Server nicht eingerichtet.", "error");
          }
          return;
        }
        setPin("");
        toast.show("Anfrage gesendet. Warte auf Bestätigung.", "info");
      } catch (error) {
        const message =
          error instanceof ApiRequestError ? error.message : "Verbindung fehlgeschlagen.";
        setPinError(message);
        setPin("");
      } finally {
        setBusy(false);
      }
    },
    [actions, busy, toast],
  );

  const handleAccept = useCallback(
    async (requestId: string) => {
      try {
        await actions.accept(requestId);
        toast.show("Verbindung hergestellt.", "success");
      } catch (error) {
        toast.show(
          error instanceof ApiRequestError ? error.message : "Bestätigen fehlgeschlagen.",
          "error",
        );
      }
    },
    [actions, toast],
  );

  const handleReject = useCallback(
    async (requestId: string) => {
      try {
        await actions.reject(requestId);
      } catch {
        // Ist die Anfrage bereits abgelaufen, ist das Ziel ohnehin erreicht.
        actions.reload();
      }
    },
    [actions],
  );

  const session = state.session;
  const incoming = state.incomingRequests[0];

  return (
    <div className="container section">
      <Eyebrow>Throw</Eyebrow>
      <h1 style={{ marginTop: "var(--space-2)" }}>Dateien werfen.</h1>

      <div style={{ marginTop: "var(--space-3)" }}>
        <ConnectionIndicator state={state.connection} />
      </div>

      {state.loadError ? (
        <Card variant="raised" style={{ marginTop: "var(--space-6)" }}>
          <h2 style={{ fontSize: "var(--text-h3)" }}>Kein Zugriff auf den Server</h2>
          <p style={{ marginTop: "var(--space-1)", color: "var(--color-granite)" }}>
            {state.loadError}
          </p>
          <div style={{ marginTop: "var(--space-3)" }}>
            <Button onClick={() => actions.reload()}>Erneut versuchen</Button>
          </div>
        </Card>
      ) : !session ? (
        <p style={{ marginTop: "var(--space-6)", color: "var(--color-granite)" }}>Lädt…</p>
      ) : session.pairing ? (
        <PairedView
          peerName={session.pairing.peerName}
          expiresAt={session.pairing.expiresAt}
          onLeave={() => void actions.leave()}
          transfers={transfers}
        />
      ) : state.outgoingRequestId ? (
        <WaitingView />
      ) : (
        <IdleView
          session={session}
          pin={pin}
          pinError={pinError}
          busy={busy}
          onPinChange={(next) => {
            setPin(next);
            setPinError(undefined);
          }}
          onConnect={handleConnect}
          onRenew={() => void actions.renewPin()}
        />
      )}

      {/*
        Eingehende Anfrage: nicht wegklickbar. Ein versehentlicher Klick neben
        das Fenster oder ein Druck auf Escape darf keine stillschweigende
        Entscheidung sein.
      */}
      <Modal
        open={Boolean(incoming) && !session?.pairing}
        onClose={() => {
          /* absichtlich leer -- die Entscheidung muss ausdrücklich fallen */
        }}
        dismissible={false}
        title="Eingehende Verbindung"
        actions={
          incoming ? (
            <>
              <Button onClick={() => void handleAccept(incoming.requestId)}>Annehmen</Button>
              <Button variant="danger" onClick={() => void handleReject(incoming.requestId)}>
                Ablehnen
              </Button>
            </>
          ) : null
        }
      >
        {incoming ? (
          <>
            <p>
              <strong>{incoming.fromName}</strong> möchte sich mit diesem Gerät verbinden.
            </p>
            <p style={{ marginTop: "var(--space-2)", color: "var(--color-granite)" }}>
              Nimm nur an, wenn du diese Verbindung erwartest. Erst danach können Dateien
              übertragen werden.
            </p>
            <div style={{ marginTop: "var(--space-2)" }}>
              <Countdown until={incoming.expiresAt} label="Läuft ab in" onExpire={actions.reload} />
            </div>
          </>
        ) : null}
      </Modal>
    </div>
  );
}

function ConnectionIndicator({ state }: { state: "connecting" | "online" | "offline" }) {
  if (state === "online") {
    return (
      <Status kind="connected" live>
        Bereit
      </Status>
    );
  }
  if (state === "connecting") {
    return (
      <Status kind="waiting" live>
        Verbinde…
      </Status>
    );
  }
  return (
    <Status kind="error" live>
      Keine Verbindung — versuche erneut
    </Status>
  );
}

function IdleView({
  session,
  pin,
  pinError,
  busy,
  onPinChange,
  onConnect,
  onRenew,
}: {
  session: { pin: string | null; pinExpiresAt: string | null; displayName: string };
  pin: string;
  pinError: string | undefined;
  busy: boolean;
  onPinChange: (value: string) => void;
  onConnect: (pin: string) => void;
  onRenew: () => void;
}) {
  return (
    <div
      style={{
        marginTop: "var(--space-8)",
        display: "grid",
        gap: "var(--space-4)",
        gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
        alignItems: "start",
      }}
    >
      <Card variant="raised">
        <Eyebrow>Deine PIN</Eyebrow>
        <p style={{ marginTop: "var(--space-1)", color: "var(--color-granite)" }}>
          Gib diese PIN am anderen Gerät ein.
        </p>

        <div style={{ marginTop: "var(--space-3)" }}>
          {session.pin ? (
            <PinDisplay pin={session.pin} label="Deine PIN" />
          ) : (
            <p style={{ color: "var(--color-granite)" }}>Wird vergeben…</p>
          )}
        </div>

        <div
          style={{
            marginTop: "var(--space-3)",
            display: "flex",
            alignItems: "center",
            gap: "var(--space-2)",
            flexWrap: "wrap",
          }}
        >
          {session.pinExpiresAt ? (
            <Countdown until={session.pinExpiresAt} label="Gültig noch" onExpire={onRenew} />
          ) : null}
          <Button variant="ghost" onClick={onRenew}>
            Neue PIN
          </Button>
        </div>

        <p
          style={{
            marginTop: "var(--space-3)",
            fontSize: "var(--text-small)",
            color: "var(--color-granite)",
          }}
        >
          Dieses Gerät erscheint beim Gegenüber als <strong>{session.displayName}</strong>.
        </p>
      </Card>

      <Card variant="raised">
        <Eyebrow>Verbinden</Eyebrow>
        <p style={{ marginTop: "var(--space-1)", color: "var(--color-granite)" }}>
          Gib die PIN des anderen Geräts ein.
        </p>

        <div style={{ marginTop: "var(--space-3)" }}>
          <PinInput
            length={4}
            value={pin}
            onChange={onPinChange}
            onComplete={onConnect}
            label="PIN des anderen Geräts"
            error={pinError}
            disabled={busy}
          />
        </div>

        <Button
          onClick={() => onConnect(pin)}
          disabled={busy || pin.length !== 4}
          style={{ marginTop: "var(--space-2)" }}
        >
          Verbinden
        </Button>
      </Card>
    </div>
  );
}

function WaitingView() {
  return (
    <Card variant="raised" style={{ marginTop: "var(--space-8)", maxWidth: "36rem" }}>
      <Eyebrow>Anfrage gesendet</Eyebrow>
      <h2 style={{ marginTop: "var(--space-1)", fontSize: "var(--text-h3)" }}>
        Warte auf Bestätigung
      </h2>
      <p style={{ marginTop: "var(--space-2)", color: "var(--color-granite)" }}>
        Das andere Gerät muss die Verbindung ausdrücklich annehmen. Bis dahin wird nichts
        übertragen.
      </p>
      <div style={{ marginTop: "var(--space-3)" }}>
        <Status kind="waiting" live>
          Warte…
        </Status>
      </div>
    </Card>
  );
}

function PairedView({
  peerName,
  expiresAt,
  onLeave,
  transfers,
}: {
  peerName: string;
  expiresAt: string;
  onLeave: () => void;
  transfers: ReturnType<typeof useTransfers>;
}) {
  return (
    <Card variant="raised" style={{ marginTop: "var(--space-8)", maxWidth: "46rem" }}>
      <Eyebrow>Verbunden</Eyebrow>
      <h2 style={{ marginTop: "var(--space-1)", fontSize: "var(--text-h3)" }}>{peerName}</h2>

      <div style={{ marginTop: "var(--space-2)" }}>
        <Countdown until={expiresAt} label="Verbindung endet in" />
      </div>

      <div style={{ marginTop: "var(--space-4)" }}>
        <TransferPanel
          files={transfers.files}
          bulk={transfers.bulk}
          actions={transfers.actions}
        />
      </div>

      <div style={{ marginTop: "var(--space-6)" }}>
        <Button variant="secondary" onClick={onLeave}>
          Verbindung trennen
        </Button>
      </div>
    </Card>
  );
}
